import { normalizeCellColor, type CellColorChange } from './cellColorUtils';

/** Saved column fill per grid and stable field id. Cell overrides take precedence. */
export type GridColumnColors = Record<string, Record<string, string>>;

export function normalizeColumnColors(value: unknown): GridColumnColors {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const grids: [string, Record<string, string>][] = [];
  for (const [gridKey, raw] of Object.entries(value)) {
    if (!gridKey.trim() || !raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const fields: [string, string][] = [];
    for (const [field, color] of Object.entries(raw)) {
      const normalized = normalizeCellColor(color);
      if (field.trim() && normalized) fields.push([field, normalized]);
    }
    if (fields.length > 0) grids.push([gridKey, Object.fromEntries(fields)]);
  }
  return Object.fromEntries(grids);
}

/** Set or clear column defaults without touching cell overrides or other columns. */
export function withColumnColors(
  config: GridColumnColors,
  gridKey: string,
  fields: readonly string[],
  color: string | null,
): GridColumnColors {
  const next = normalizeColumnColors(config);
  if (!gridKey.trim()) return next;
  const grid = { ...next[gridKey] };
  const normalized = normalizeCellColor(color);
  for (const field of fields) {
    if (!field.trim()) continue;
    if (normalized) Object.defineProperty(grid, field, { value: normalized, enumerable: true, writable: true, configurable: true });
    else delete grid[field];
  }
  if (Object.keys(grid).length > 0) Object.defineProperty(next, gridKey, { value: grid, enumerable: true, writable: true, configurable: true });
  else delete next[gridKey];
  return next;
}

export function effectiveFillColor(cell: string | undefined, column: string | undefined, fallback: string): string {
  return normalizeCellColor(cell) ?? normalizeCellColor(column) ?? fallback;
}

/** Reset every stored override of these columns, including non-visible rows and subtotals. */
export function columnCellColorChanges(colors: ReadonlyMap<string, string>, fields: readonly string[]): CellColorChange[] {
  const selected = new Set(fields);
  const changes: CellColorChange[] = [];
  for (const key of colors.keys()) {
    const separator = key.indexOf('|');
    if (separator < 1 || separator === key.length - 1) continue;
    const field = key.slice(0, separator);
    if (selected.has(field)) changes.push({ field, rowKey: key.slice(separator + 1), color: null });
  }
  return changes;
}
